// FilterPill — clickable filter chip with optional count badge. Used by
// specs 43 (admin templates), 46 (resources directory) and 47 (public
// pages directory) for category / status filter rows.

import type { ReactNode } from 'react';

export interface FilterPillProps {
  label: ReactNode;
  /** Optional numeric count rendered as a subtle suffix. */
  count?: number;
  /** Whether this pill is the active filter. */
  active?: boolean;
  /** Whether the pill is disabled (e.g. count is 0). */
  disabled?: boolean;
  onClick?: () => void;
  /** Optional secondary count (e.g. populated/missing in spec 46). */
  secondary?: string;
  className?: string;
}

export function FilterPill({
  label,
  count,
  active = false,
  disabled = false,
  onClick,
  secondary,
  className = '',
}: FilterPillProps) {
  const base =
    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors';
  const state = active
    ? 'border-stone-900 bg-stone-900 text-white'
    : disabled
      ? 'border-stone-200 bg-stone-50 text-stone-400 cursor-not-allowed'
      : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-100 hover:border-stone-400';

  return (
    <button
      type="button"
      data-component="filter-pill"
      data-active={active}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`${base} ${state} ${className}`}
    >
      <span>{label}</span>
      {count != null && (
        <span
          className={`tabular-nums ${
            active ? 'text-white/80' : 'text-stone-500'
          }`}
        >
          {count}
        </span>
      )}
      {secondary && (
        <span
          className={`tabular-nums text-[10px] ${
            active ? 'text-white/70' : 'text-stone-400'
          }`}
        >
          {secondary}
        </span>
      )}
    </button>
  );
}
